'use client';

import type { ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Avatar } from '@/components/ui/Avatar';
import { buttonVariants } from '@/components/ui/button';
import { Link } from '@/components/ui/link';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Numeric } from '@/components/ui/numeric';
import { MatchScoreHeader } from '@/components/match/MatchScoreHeader';
import { MatchScoreSheet } from '@/components/match/MatchScoreSheet';
import { MatchWhoLed } from '@/components/match/MatchWhoLed';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import { StreamPlayer } from '@/components/streaming/StreamPlayer';
import { StreamLinkCard } from '@/components/streaming/StreamLinkCard';
import { useNameFormatting } from '@/contexts/NameFormattingContext';
import { useLiveMatch } from '@/hooks/useLiveMatch';
import { cn, formatDuration } from '@/lib/utils';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import {
  cardState,
  matchCardStatus,
  type MatchPhase,
} from '@/app/[locale]/event/[eventId]/libs/matchCardStatus';
import { rosterLines, type TeamEntry } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import {
  initialsFromLabel,
  resolveParticipantLabel,
} from '@/app/[locale]/event/[eventId]/libs/participantLabel';
import { leadPath } from '../../libs/matchHighlights';
import type { ScorecardContext, ScorecardLiveFeeds } from '../../libs/loadScorecardContext';
import { scorecardVideo, type ScorecardReplay } from '../../libs/scorecardVideo';
import { matchFormatOf, sheetGamesFor, type MatchWithGames } from '../../libs/scoreSheet';
import type { ScorecardPlayer } from '../../libs/scorecardPlayer';
import MatchLiveFeeds from './MatchLiveFeeds';

interface ScorecardClientProps {
  match: MatchWithGames;
  player1: ScorecardPlayer;
  player2: ScorecardPlayer;
  /** Team-division entry for each side (`event_group.is_team = true`); null/absent → personal name. */
  player1Team?: TeamEntry | null;
  player2Team?: TeamEntry | null;
  context: ScorecardContext;
  /** The match's court on its day. */
  liveFeeds: ScorecardLiveFeeds | null;
  /** The match's place in a public app recording, or null when it has none. */
  replay: ScorecardReplay | null;
  /** The viewer is the player who scored this match. */
  canScoreAgain: boolean;
}

// One card surface for the sections the shared components draw without chrome. The sheet's root
// is a container-query context, which collapses in a shrink-to-fit parent, so each section is
// given a real width. `empty:hidden` drops the card when the sheet or chart renders nothing.
const SECTION_CARD =
  'bg-card border-border-strong w-full min-w-0 overflow-hidden rounded-lg border shadow-sm empty:hidden';

const ACTION_BASE = 'min-h-11 px-4 py-2 text-sm font-semibold @md:min-h-9';

export default function ScorecardClient({
  match: initialMatch,
  player1,
  player2,
  player1Team,
  player2Team,
  context,
  liveFeeds,
  replay,
  canScoreAgain,
}: ScorecardClientProps) {
  const t = useTranslations('Scorecard');
  const tMatches = useTranslations('Event.matches');
  const tMatchesCommon = useTranslations('Matches');
  const locale = useLocale();
  const outcomeLabel = useOutcomeLabel();
  const { isJapanese } = useNameFormatting();
  const live = useLiveMatch({ matchId: initialMatch.id, initialMatch });
  const match = live.match ?? initialMatch;
  const isCompleted = match.status === 'completed';
  const isLive = match.status === 'in_progress';

  // Live: the games come from app_state with the save chain's rules, so the page shows exactly
  // what finalize will persist. Otherwise the stored rows.
  const { sheetGames, gamesWon } = sheetGamesFor(match);
  const { bestOf, pointsPerGame } = matchFormatOf(match);
  // The game being played, never the finished one a live match sits between.
  const playing = isLive ? sheetGames.find((g) => g.inProgress) : undefined;
  const currentGame = playing ? { number: playing.number, score: playing.score } : undefined;

  // The winner: the stored one, else whoever took more games.
  const winner: 0 | 1 | null = !isCompleted
    ? null
    : match.winner_id
      ? match.winner_id === match.player_1_id
        ? 0
        : match.winner_id === match.player_2_id
          ? 1
          : null
      : gamesWon[0] > gamesWon[1]
        ? 0
        : gamesWon[1] > gamesWon[0]
          ? 1
          : null;

  const names: [string, string] = [
    resolveParticipantLabel({ entry: player1Team, player: player1, isJapanese }),
    resolveParticipantLabel({ entry: player2Team, player: player2, isJapanese }),
  ];
  // One initials rule with the Watch panel: both read the displayed name.
  const initials: [string, string] = [initialsFromLabel(names[0]), initialsFromLabel(names[1])];
  const teams = [player1Team, player2Team] as const;
  const players = [player1, player2] as const;

  // One status ladder with Matches and Watch: a winnerless outcome reads before the phase.
  // The outcome lives on the schedule row (a retirement also on the match row itself).
  const outcome = match.retired_player_id ? 'retired' : context.outcome;
  const winnerless = isWinnerlessRow({ outcome, is_walkover: context.isWalkover });
  const phase: MatchPhase = isLive ? 'live' : isCompleted ? 'finished' : 'upcoming';
  const state = cardState(winnerless, phase);
  const { statusWord, status } = matchCardStatus({
    state,
    outcomeWord: state === 'winnerless' ? outcomeLabel(outcome, context.isWalkover) : null,
    retired: outcome === 'retired',
    words: {
      live: tMatches('statusLive'),
      upcoming: tMatches('statusUpcoming'),
      finished: tMatches('statusFinished'),
      retiredShort: tMatchesCommon('retiredShort'),
    },
  });

  // The match length, as it has always been worked out; none while it is being played.
  const matchStartTime = new Date(match.played_at);
  const matchEndTime = match.updated_at ? new Date(match.updated_at) : new Date();
  const durationSeconds = (matchEndTime.getTime() - matchStartTime.getTime()) / 1000;
  const startsAt = context.startsAt ?? match.played_at;
  const showDuration = !isLive && Number.isFinite(durationSeconds) && durationSeconds > 0;

  const rich = (chunks: ReactNode) => <Numeric>{chunks}</Numeric>;
  const decidingPoints = bestOf > 1 ? match.deciding_game_points : null;
  const footer = (
    <p className="m-0">
      {context.locationName && (
        <>
          <span className={NAME_WRAP}>{context.locationName}</span>
          {' · '}
        </>
      )}
      {t.rich('matchFormat', { points: pointsPerGame, games: bestOf, n: rich })}
      {decidingPoints != null && (
        <>
          {' · '}
          {t.rich('decidingTarget', { points: decidingPoints, n: rich })}
        </>
      )}
    </p>
  );

  const scoreAgainHref = context.eventId
    ? `/match/score-choice/event/${context.eventId}/match`
    : '/match/score-choice';
  const showScoreAgain = canScoreAgain && isCompleted;
  const action = showScoreAgain ? (
    <div className="flex flex-col gap-2 @md:flex-row @md:items-center">
      <Link
        href={scoreAgainHref}
        data-score-again
        className={cn(buttonVariants.base, buttonVariants.variants.variant.secondary, ACTION_BASE)}
      >
        {t('scoreAgain')}
      </Link>
    </div>
  ) : undefined;

  const side = (i: 0 | 1) => ({
    name: names[i],
    roster: rosterLines(teams[i]?.roster),
    avatar: (
      <Avatar
        name={names[i]}
        photoUrl={players[i].profile_photo}
        size="md"
        className="@md:h-12 @md:w-12 @md:text-sm"
      />
    ),
    won: winner === i,
    lost: winner !== null && winner !== i,
  });

  const hasLead = sheetGames.some((g) => leadPath(g).length > 0);
  // The live player shows only while the match is live (a match that starts while open gains it);
  // the replay is read on the server, so a match that ends while open shows it on reload.
  const video = scorecardVideo(phase, liveFeeds?.feeds ?? [], replay);
  const videoTitle = context.courtName ?? names.join(` ${t('versus')} `);

  return (
    <div className="mx-auto flex w-full max-w-5xl min-w-0 flex-col gap-3">
      <h1 className="sr-only">{`${names[0]} ${t('versus')} ${names[1]}`}</h1>
      {video.kind !== 'none' && (
        <div data-scorecard-video={video.kind} className="mx-auto w-full max-w-3xl min-w-0">
          {video.kind === 'live' && <MatchLiveFeeds feeds={video.feeds} />}
          {video.kind === 'replay' && (
            <StreamPlayer
              key={`${video.feed.id}:${video.startSec}`}
              feed={video.feed}
              title={videoTitle}
              startSec={video.startSec}
            />
          )}
          {video.kind === 'link' && (
            <StreamLinkCard feed={video.feed} title={videoTitle} compact live={isLive} />
          )}
        </div>
      )}

      <div className="w-full min-w-0">
        <MatchScoreHeader
          strip={{
            court: context.courtName ?? undefined,
            division: context.divisionName ?? undefined,
            status,
            statusWord,
            state,
            meta: (
              <>
                {startsAt && <LocalDateTime iso={startsAt} locale={locale} />}
                {showDuration && (
                  <>
                    {startsAt && ' · '}
                    <Numeric>{formatDuration(durationSeconds)}</Numeric>
                  </>
                )}
              </>
            ),
          }}
          sides={[side(0), side(1)]}
          gamesWon={gamesWon}
          currentGame={currentGame}
          footer={footer}
          action={action}
        />
      </div>

      <div className={SECTION_CARD}>
        <MatchScoreSheet
          games={sheetGames}
          sides={initials}
          showServeBox
          density="page"
          live={isLive}
        />
      </div>

      {hasLead && (
        <div className={SECTION_CARD}>
          <MatchWhoLed games={sheetGames} sides={names} />
        </div>
      )}
    </div>
  );
}
